import { PhoneInput } from "@/components/shared/PhoneInput";
import { SearchSelect } from "@/components/shared/SearchSelect";

import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { FileText, Upload, Trash2, Download, Briefcase, ShieldCheck } from "lucide-react";
import api, { API_BASE, formatApiError, downloadOfferPdf, CANCELLED_OFFER } from "@/lib/api";
import { PortalNav } from "@/components/layout/PortalNav";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { DOC_TYPES } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";

const TABS = ["Applications", "Offers", "Profile"];

export default function CandidatePortal() {
  const { user, setUser } = useAuth();
  const [tab, setTab] = useState("Applications");
  const [applications, setApplications] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [offers, setOffers] = useState([]);

  const load = async () => {
    const [a, d, o] = await Promise.all([
      api.get("/candidate/applications"),
      api.get("/candidate/documents"),
      api.get("/candidate/offers"),
    ]);
    setApplications(a.data);
    setDocuments(d.data);
    setOffers(o.data);
  };

  useEffect(() => { load().catch(() => {}); }, []);

  return (
    <div className="min-h-screen bg-[var(--aur-bg)]">
      <PortalNav />
      <div className="mx-auto max-w-6xl px-6 py-12 lg:px-8">
        <p className="eyebrow">Welcome back</p>
        <h1 className="mt-3 font-serif text-3xl tracking-tight sm:text-4xl">{user?.name}</h1>
        <p className="mt-2 font-sans text-sm text-[var(--aur-text-2)]">Track applications, manage documents, and review offers.</p>

        {/* Stat cards */}
        <div className="mt-10 grid gap-4 sm:grid-cols-3">
          <StatCard label="Active Applications" value={applications.filter((a) => !["Rejected", "Completed"].includes(a.status)).length} />
          <StatCard label="Documents on File" value={documents.length} />
          <StatCard label="Offers Issued" value={offers.filter((o) => !CANCELLED_OFFER.includes(o.status)).length} />
        </div>

        {/* Tabs */}
        <div className="mt-10 flex gap-2 border-b border-[var(--aur-border)]">
          {TABS.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`portal-tab-${t.toLowerCase()}`}
              className={`relative px-4 py-3 font-sans text-sm transition-colors ${tab === t ? "text-gold" : "text-[var(--aur-text-2)] hover:text-[var(--aur-text)]"}`}
            >
              {t}
              {tab === t && <span className="absolute bottom-0 left-0 h-0.5 w-full bg-gold" />}
            </button>
          ))}
        </div>

        <div className="mt-8">
          {tab === "Applications" && <ApplicationsTab applications={applications} />}
          {tab === "Offers" && <OffersTab offers={offers} reload={load} />}
          {tab === "Profile" && <ProfileTab user={user} setUser={setUser} />}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="rounded-lg border border-[var(--aur-border)] bg-[var(--aur-card)] p-6">
      <p className="font-mono text-[10px] uppercase tracking-widest text-[var(--aur-text-2)]">{label}</p>
      <p className="mt-3 font-serif text-4xl text-gold">{value}</p>
    </div>
  );
}

function ApplicationsTab({ applications }) {
  if (applications.length === 0)
    return (
      <div className="rounded-lg border border-dashed border-[var(--aur-border)] p-12 text-center">
        <Briefcase className="mx-auto text-[var(--aur-text-2)]" />
        <p className="mt-4 font-sans text-sm text-[var(--aur-text-2)]">No applications yet.</p>
        <Link to="/careers" className="mt-4 inline-block rounded-full bg-gold px-5 py-2 font-sans text-xs font-semibold uppercase tracking-widest text-[var(--aur-bg)]">Browse Openings</Link>
      </div>
    );
  return (
    <div className="space-y-4">
      {applications.map((a) => (
        <Link key={a.id} to={`/portal/applications/${a.id}`} data-testid={`app-row-${a.id}`} className="flex items-center justify-between rounded-lg border border-[var(--aur-border)] bg-[var(--aur-card)] p-5 transition-all hover:border-gold/40">
          <div>
            <div className="flex items-center gap-3">
              <h3 className="font-serif text-xl">{a.job_title}</h3>
              <StatusBadge status={a.status} />
            </div>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-[var(--aur-text-2)]">{a.reference} · {a.department} · {a.application_path}</p>
          </div>
          <span className="font-mono text-[11px] uppercase tracking-widest text-gold">View →</span>
        </Link>
      ))}
    </div>
  );
}

function DocumentsTab({ documents, reload }) {
  const fileRef = useRef();
  const [docType, setDocType] = useState(DOC_TYPES[0]);
  const [uploading, setUploading] = useState(false);

  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("doc_type", docType);
    try {
      await api.post("/candidate/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded securely");
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const view = async (doc) => {
    try {
      const res = await api.get(`/candidate/documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error("Could not open document");
    }
  };

  const del = async (doc) => {
    try {
      await api.delete(`/candidate/documents/${doc.id}`);
      toast.success("Document removed");
      reload();
    } catch {
      toast.error("Failed to remove");
    }
  };

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-end gap-3 rounded-lg border border-[var(--aur-border)] bg-[var(--aur-card)] p-5">
        <div className="flex-1">
          <label className="eyebrow mb-2 block">Document type</label>
          <SearchSelect value={docType} onChange={(e) => setDocType(e.target.value)} data-testid="doc-type-select" className="w-full rounded-md border border-[var(--aur-border)] bg-[var(--aur-bg)] px-3 py-2.5 font-sans text-sm text-[var(--aur-text)] focus:border-gold focus:outline-none">
            {DOC_TYPES.map((t) => <option key={t}>{t}</option>)}
          </SearchSelect>
        </div>
        <input ref={fileRef} type="file" onChange={upload} accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" className="hidden" data-testid="candidate-doc-upload-input" />
        <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="doc-upload-btn" className="flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 font-sans text-xs font-semibold uppercase tracking-widest text-[var(--aur-bg)] hover:bg-gold-bright disabled:opacity-60">
          <Upload size={14} /> {uploading ? "Uploading…" : "Upload"}
        </button>
      </div>
      <p className="mb-4 font-sans text-xs text-[var(--aur-text-2)]">Private storage · PDF, JPG, PNG, DOC/DOCX · max 10MB. Only you and HR can access these.</p>

      {documents.length === 0 ? (
        <p className="rounded-lg border border-dashed border-[var(--aur-border)] p-12 text-center font-sans text-sm text-[var(--aur-text-2)]">Your document vault is empty.</p>
      ) : (
        <div className="space-y-3">
          {documents.map((d) => (
            <div key={d.id} data-testid={`doc-row-${d.id}`} className="flex items-center justify-between rounded-lg border border-[var(--aur-border)] bg-[var(--aur-card)] p-4">
              <div className="flex items-center gap-3">
                <FileText size={18} className="text-gold" />
                <div>
                  <p className="font-sans text-sm text-[var(--aur-text)]">{d.original_filename}</p>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-[var(--aur-text-2)]">{d.doc_type} · v{d.version} · {(d.size / 1024).toFixed(0)} KB</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => view(d)} data-testid={`doc-view-${d.id}`} className="rounded-md border border-[var(--aur-border)] p-2 text-[var(--aur-text-2)] hover:border-gold hover:text-gold"><Download size={15} /></button>
                <button onClick={() => del(d)} data-testid={`doc-delete-${d.id}`} className="rounded-md border border-[var(--aur-border)] p-2 text-[var(--aur-text-2)] hover:border-rose-500 hover:text-rose-400"><Trash2 size={15} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function OffersTab({ offers, reload }) {
  const respond = async (offer, decision) => {
    try {
      await api.post(`/candidate/offers/${offer.id}/respond`, { decision });
      toast.success(`Offer ${decision.toLowerCase()}`);
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };

  if (offers.length === 0)
    return <p className="rounded-lg border border-dashed border-[var(--aur-border)] p-12 text-center font-sans text-sm text-[var(--aur-text-2)]">No offers have been issued to you yet.</p>;

  return (
    <div className="space-y-6">
      {offers.map((o) => {
        const v = o.versions?.[0];
        const cancelled = CANCELLED_OFFER.includes(o.status);
        return (
          <div key={o.id} data-testid={`offer-${o.id}`} className="rounded-lg border border-[var(--aur-border)] bg-[var(--aur-card)] p-6">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <ShieldCheck className="text-gold" size={18} />
                <h3 className="font-serif text-xl">{o.job_title}</h3>
                <StatusBadge status={cancelled ? "Cancelled" : o.status} />
              </div>
              <button onClick={() => downloadOfferPdf(`/candidate/offers/${o.id}/pdf`, `Offer_${o.job_title}.pdf`).catch(() => toast.error("Download failed"))} data-testid={`offer-download-${o.id}`} className="flex items-center gap-1.5 rounded-full border border-gold/40 px-4 py-2 font-sans text-xs font-semibold uppercase tracking-widest text-gold hover:bg-gold/10"><Download size={13} /> Download PDF</button>
            </div>
            {cancelled && <p data-testid={`offer-cancelled-${o.id}`} className="mb-3 rounded-md border border-rose-500/30 bg-rose-500/10 px-4 py-2 font-sans text-sm text-rose-300">This offer has been cancelled and is no longer valid.</p>}
            <div className={`relative overflow-hidden rounded-md border border-[var(--aur-border)] bg-white p-6 text-black ${cancelled ? "opacity-70" : ""}`} dangerouslySetInnerHTML={{ __html: v?.html || "" }} />
            {o.status === "Sent" && (
              <div className="mt-5 flex gap-3">
                <button onClick={() => respond(o, "Accepted")} data-testid={`offer-accept-${o.id}`} className="rounded-full bg-emerald-500 px-6 py-2.5 font-sans text-xs font-semibold uppercase tracking-widest text-white hover:bg-emerald-400">Accept Offer</button>
                <button onClick={() => respond(o, "Rejected")} data-testid={`offer-reject-${o.id}`} className="rounded-full border border-rose-500/50 px-6 py-2.5 font-sans text-xs font-semibold uppercase tracking-widest text-rose-400 hover:bg-rose-500/10">Decline</button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ProfileTab({ user, setUser }) {
  const [form, setForm] = useState({
    name: user?.name || "", phone: user?.phone || "", nationality: user?.nationality || "",
    current_location: user?.current_location || "", headline: user?.headline || "",
    years_experience: user?.years_experience || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, years_experience: form.years_experience ? Number(form.years_experience) : null };
      const { data } = await api.put("/candidate/profile", payload);
      setUser(data);
      toast.success("Profile saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const cls = "w-full rounded-md border border-[var(--aur-border)] bg-[var(--aur-bg)] px-3 py-2.5 font-sans text-sm text-[var(--aur-text)] focus:border-gold focus:outline-none";

  return (
    <div className="max-w-2xl rounded-lg border border-[var(--aur-border)] bg-[var(--aur-card)] p-6">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Full name"><input value={form.name} onChange={set("name")} className={cls} data-testid="profile-name" /></Field>
        <Field label="Phone"><PhoneInput value={form.phone} onChange={(v) => setForm((p) => ({ ...p, phone: v }))} className={cls} testid="profile-phone" /></Field>
        <Field label="Nationality"><input value={form.nationality} onChange={set("nationality")} className={cls} data-testid="profile-nationality" /></Field>
        <Field label="Current location"><input value={form.current_location} onChange={set("current_location")} className={cls} data-testid="profile-location" /></Field>
        <Field label="Years of experience"><input type="number" value={form.years_experience} onChange={set("years_experience")} className={cls} data-testid="profile-experience" /></Field>
        <Field label="Email (read-only)"><input value={user?.email} disabled className={`${cls} opacity-60`} /></Field>
      </div>
      <Field label="Professional headline" className="mt-4"><input value={form.headline} onChange={set("headline")} className={cls} data-testid="profile-headline" placeholder="e.g. Michelin-trained pastry chef" /></Field>
      <button onClick={save} disabled={saving} data-testid="profile-save" className="mt-6 rounded-full bg-gold px-6 py-2.5 font-sans text-xs font-semibold uppercase tracking-widest text-[var(--aur-bg)] hover:bg-gold-bright disabled:opacity-60">
        {saving ? "Saving…" : "Save Profile"}
      </button>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="eyebrow mb-2 block">{label}</label>
      {children}
    </div>
  );
}
